import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2, CalendarCheck, BookOpen, Briefcase, Sparkles } from "lucide-react";
import { useDialog } from "./shared";
import { createCheckout } from "../../lib/api";

const Overline = ({ children }) => <span className="overline">{children}</span>;

const CREDIT_PLANS = [
  { lookup: "free", name: "Free Credit Consultation", price: "FREE", period: "",
    desc: "Start with a complimentary 1-on-1. We review your situation and recommend the best path.",
    features: ["Personalized consultation", "Goal assessment", "Service recommendations", "Q&A"], cta: "Book free consult" },
  { lookup: "bgm_monthly", name: "Monthly Credit Success Plan", price: "$99.99", period: "/mo",
    desc: "Flexibility & ongoing support with no long-term commitment. + Active credit monitoring.",
    features: ["Personalized credit review", "Monthly dispute assistance", "Progress updates", "Client portal access", "Educational resources", "Dedicated support"], cta: "Subscribe" },
  { lookup: "bgm_starter", name: "Starter Clean-Up Plan", price: "$149.99", period: "",
    desc: "A 3-month program for lighter credit challenges — an affordable way to begin.",
    features: ["3 months of credit repair", "90 days credit monitoring", "Customized dispute strategy", "Progress tracking", "Educational resources"], cta: "Enroll now" },
  { lookup: "bgm_standard", name: "Standard Rebuild Plan", price: "$399.99", period: "",
    desc: "A 6-month rebuilding program for more established credit challenges.",
    features: ["6 months of service", "180 days credit monitoring", "Ongoing dispute assistance", "Progress tracking", "Client portal access", "Educational resources"], cta: "Enroll now", featured: true },
  { lookup: "bgm_vip_elite", name: "VIP Elite Plan", price: "$689.99", period: "",
    desc: "A premium 9-month program with priority support & strategic planning.",
    features: ["9 months of service", "274 days credit monitoring", "Strategic improvement plan", "Priority support", "Monthly updates", "Educational resources"], cta: "Go VIP" },
  { lookup: "bgm_transformation", name: "BGM Credit Transformation Plan", price: "$989.99", period: "",
    desc: "Our most comprehensive program — a full year of personalized support.",
    features: ["12 months of service", "365 days credit monitoring", "Personalized dispute management", "Ongoing strategy reviews", "Priority support", "Client portal + resources"], cta: "Transform my credit" },
];

const FUNDING = [
  { lookup: "bgm_funding_consult", name: "Business Funding Consultation", price: "$97",
    desc: "Discuss your funding goals, review readiness, and get guidance on future financing.",
    features: ["Funding readiness review", "Business assessment", "Personalized recommendations", "Action plan"] },
  { lookup: "bgm_funding_prep", name: "Business Funding Preparation Package", price: "$497",
    desc: "Educational guidance, organization tools & a custom strategy to strengthen readiness.",
    features: ["Funding strategy session", "Business profile review", "Funding prep checklist", "Educational resources", "Personalized action plan"] },
];

const PRODUCTS = [
  { lookup: "bgm_credit_bible", name: "The Credit Bible", price: "$27", desc: "Beginner-friendly digital guide to how credit works and stronger financial habits." },
  { lookup: "bgm_diy_kit", name: "DIY Credit Repair Starter Kit", price: "$47", desc: "Worksheets, checklists & resources to organize your credit journey." },
  { lookup: "bgm_dispute_templates", name: "Credit Dispute Letter Template Bundle", price: "$37", desc: "Editable educational templates & guidance on dispute correspondence." },
  { lookup: "bgm_biz_credit_guide", name: "Business Credit Starter Guide", price: "$47", desc: "Fundamentals of business credit, organization & growth preparation." },
  { lookup: "bgm_freedom_bundle", name: "Financial Freedom Bundle", price: "$97", desc: "Our complete education bundle — all guides, worksheets & checklists in one.", bundle: true },
];

const ADDITIONAL = [
  { lookup: "bgm_credit_education", name: "Credit Education Session", price: "$49", desc: "1-on-1 session to understand your report, scores & credit-building strategies." },
  { lookup: "bgm_credit_analysis", name: "Credit Analysis", price: "$79", desc: "Detailed review of your credit profile with personalized next-step recommendations." },
  { lookup: "bgm_financial_strategy", name: "Financial Strategy Consultation", price: "$149", desc: "Personalized consultation on goals, credit profile & actionable strategies." },
];

function useBuy() {
  const { openBooking } = useDialog();
  const [loading, setLoading] = useState(null);
  const buy = async (item) => {
    if (item.lookup === "free") { openBooking("Free Credit Consultation", "Book your free consultation"); return; }
    setLoading(item.lookup);
    try {
      const { checkout_url } = await createCheckout({ lookup_key: item.lookup, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch { setLoading(null); }
  };
  return { buy, loading };
}

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

export default function Catalog() {
  const { buy, loading } = useBuy();
  const Btn = ({ item, featured }) => (
    <button data-testid={`buy-${item.lookup}`} onClick={() => buy(item)} disabled={loading === item.lookup}
      className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 font-body text-sm font-semibold transition-colors ${featured ? "bg-[#d4af37] text-[#050505] hover:bg-[#f3e5ab]" : "border border-[#d4af37]/40 text-gold hover:bg-[#d4af37]/10"}`}>
      {loading === item.lookup ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{item.cta || (item.lookup === "free" ? "Book" : "Buy now")} <ArrowRight className="h-4 w-4" /></>}
    </button>
  );

  return (
    <section id="pricing" className="relative px-6 py-28">
      <div className="spotlight pointer-events-none absolute inset-x-0 top-0 h-1/3" />
      <div className="mx-auto max-w-6xl">
        {/* Credit Repair */}
        <Reveal className="mb-12 text-center">
          <Overline>Services & pricing</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Credit Repair Services</h2>
        </Reveal>
        <div className="grid gap-6 lg:grid-cols-3">
          {CREDIT_PLANS.map((p, i) => (
            <Reveal key={p.lookup} delay={(i % 3) * 0.08}>
              <div data-testid={`plan-${p.lookup}`} className={`relative flex h-full flex-col rounded-3xl p-8 ${p.featured ? "gold-border-glow bg-[#0f0f10]" : "glass"}`}>
                {p.featured && <span className="absolute -top-3 left-8 rounded-full bg-[#d4af37] px-3 py-1 font-body text-[10px] font-bold tracking-widest text-[#050505]">MOST POPULAR</span>}
                {p.lookup === "free" && <span className="absolute -top-3 left-8 rounded-full bg-emerald-500 px-3 py-1 font-body text-[10px] font-bold tracking-widest text-[#050505]">FREE</span>}
                <h3 className="font-display text-lg text-[#f5f5f5]">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-light gold-text-gradient">{p.price}</span>
                  <span className="font-body text-sm text-[#888]">{p.period}</span>
                </div>
                <p className="mt-3 font-body text-sm text-[#a3a3a3]">{p.desc}</p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 font-body text-sm text-[#c9c9c9]"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {f}</li>
                  ))}
                </ul>
                <Btn item={p} featured={p.featured || p.lookup === "free"} />
              </div>
            </Reveal>
          ))}
        </div>

        {/* Business Funding */}
        <Reveal className="mb-10 mt-24 text-center">
          <span className="mb-3 inline-flex items-center gap-2 text-gold"><Briefcase className="h-5 w-5" /></span>
          <h2 className="font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-4xl">Business Funding Services</h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {FUNDING.map((p, i) => (
            <Reveal key={p.lookup} delay={i * 0.1}>
              <div className="glass flex h-full flex-col rounded-3xl p-8" data-testid={`plan-${p.lookup}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-lg text-[#f5f5f5]">{p.name}</h3>
                  <span className="font-display text-3xl font-light gold-text-gradient">{p.price}</span>
                </div>
                <p className="mt-3 font-body text-sm text-[#a3a3a3]">{p.desc}</p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2 font-body text-sm text-[#c9c9c9]"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {f}</li>)}
                </ul>
                <Btn item={p} />
              </div>
            </Reveal>
          ))}
        </div>

        {/* Digital Products */}
        <Reveal className="mb-10 mt-24 text-center">
          <span className="mb-3 inline-flex items-center gap-2 text-gold"><BookOpen className="h-5 w-5" /></span>
          <h2 className="font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-4xl">Digital Products</h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.lookup} delay={(i % 3) * 0.08}>
              <div className={`flex h-full flex-col rounded-2xl p-7 ${p.bundle ? "gold-border-glow bg-[#0f0f10]" : "glass"}`} data-testid={`product-${p.lookup}`}>
                {p.bundle && <span className="mb-2 inline-flex items-center gap-1 font-body text-[10px] font-bold tracking-widest text-gold"><Sparkles className="h-3 w-3" /> BEST VALUE</span>}
                <h3 className="font-display text-base text-[#f5f5f5]">{p.name}</h3>
                <span className="mt-2 font-display text-3xl font-light gold-text-gradient">{p.price}</span>
                <p className="mt-3 flex-1 font-body text-sm text-[#a3a3a3]">{p.desc}</p>
                <Btn item={p} featured={p.bundle} />
              </div>
            </Reveal>
          ))}
        </div>

        {/* Additional Services */}
        <Reveal className="mb-10 mt-24 text-center">
          <span className="mb-3 inline-flex items-center gap-2 text-gold"><CalendarCheck className="h-5 w-5" /></span>
          <h2 className="font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-4xl">Additional Services</h2>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {ADDITIONAL.map((p, i) => (
            <Reveal key={p.lookup} delay={i * 0.08}>
              <div className="glass flex h-full flex-col rounded-2xl p-7" data-testid={`service-item-${p.lookup}`}>
                <h3 className="font-display text-base text-[#f5f5f5]">{p.name}</h3>
                <span className="mt-2 font-display text-3xl font-light gold-text-gradient">{p.price}</span>
                <p className="mt-3 flex-1 font-body text-sm text-[#a3a3a3]">{p.desc}</p>
                <Btn item={p} />
              </div>
            </Reveal>
          ))}
        </div>

        {/* Important Information */}
        <Reveal className="mt-20">
          <div className="glass rounded-3xl p-8">
            <h3 className="font-display text-lg text-[#f5f5f5]">Important Information</h3>
            <div className="mt-4 space-y-3 font-body text-sm text-[#a3a3a3]">
              <p><span className="text-gold">Credit Monitoring Requirement:</span> Active credit monitoring is required for all credit repair service plans.</p>
              <p><span className="text-gold">Secure Client Portal:</span> Every client receives access to a secure portal for onboarding, document uploads, progress updates and communication.</p>
              <p><span className="text-gold">Educational Commitment:</span> BGM is committed to transparent, educational services. While we work diligently to support your journey, individual results vary based on each client's circumstances and participation.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
